import { z } from 'zod';
/**
 * Audit & sức khoẻ hệ thống — APM-403.
 *
 * Hai viewer khác nhau về đối tượng đọc, và đó là chủ đích:
 * - `event_log` trả lời "đã có gì xảy ra trong tổ chức" — dành cho quản trị.
 * - `sensitive_access_log` trả lời "ai đã xem hồ sơ hiệu suất của ai" — dành
 *   cho quản trị **và** cho chính người bị xem (docs/security.md §5, spec mục
 *   14: minh bạch với người bị đánh giá).
 */
export const ActorType = z.enum(['user', 'ai', 'system']);
export const AuditEventView = z.object({
    /** `event_log.id` — BigInt dạng chuỗi để JSON không mất chính xác. */
    id: z.string(),
    aggregateType: z.string(),
    aggregateId: z.uuid(),
    seq: z.number().int().positive(),
    eventType: z.string(),
    projectId: z.uuid().nullable(),
    actorType: ActorType,
    actorId: z.uuid().nullable(),
    /** Tên người gây ra thay đổi, `null` khi là `system` hoặc user đã bị xoá. */
    actorName: z.string().nullable(),
    /** Gom nhiều event của cùng một thao tác — vd duyệt một CR. */
    correlationId: z.uuid().nullable(),
    payload: z.unknown(),
    occurredAt: z.iso.datetime(),
});
export const AuditEventQuery = z.object({
    aggregateType: z.string().max(40).optional(),
    eventType: z.string().max(60).optional(),
    actorId: z.uuid().optional(),
    projectId: z.uuid().optional(),
    correlationId: z.uuid().optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    /** Cursor là `event_log.id` của dòng cuối trang trước — docs/api-conventions.md §4. */
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const AuditEventPage = z.object({
    items: z.array(AuditEventView),
    page: z.object({
        cursor: z.string().nullable(),
        hasMore: z.boolean(),
        limit: z.number().int().positive(),
    }),
    /**
     * Cảnh báo giới hạn của viewer.
     *
     * Event cũ hơn `oldestAvailable` đã được archive lên S3 (APM-203) và **không**
     * nằm trong kết quả này. Bày ra thay vì im lặng trả về ít dòng hơn: một viewer
     * audit hiện thiếu dữ liệu mà không nói gì là tệ hơn không có viewer.
     */
    retention: z.object({
        oldestAvailable: z.iso.datetime().nullable(),
        archivedPeriods: z.array(z.string()),
    }),
});
export const SensitiveAccessView = z.object({
    id: z.uuid(),
    viewerId: z.uuid(),
    viewerName: z.string().nullable(),
    subjectId: z.uuid(),
    subjectName: z.string().nullable(),
    scope: z.string(),
    reason: z.string().nullable(),
    accessedAt: z.iso.datetime(),
});
/** Sức khoẻ hệ thống cho màn hình quản trị. */
export const SystemHealthView = z.object({
    version: z.string(),
    nodeVersion: z.string(),
    uptimeSec: z.number().nonnegative(),
    environment: z.string(),
    checks: z.object({
        database: z.enum(['up', 'down']),
        /**
         * ⚠️ Có mặt ở **màn hình chẩn đoán** nhưng KHÔNG có ở `/readyz`. Đưa S3 vào
         * readiness là bootstrap deadlock: pod không ready ⇒ admin không vào được
         * UI ⇒ không cấu hình được S3 ⇒ pod mãi không ready (ADR-003).
         */
        storage: z.enum(['configured', 'not_configured']),
        ai: z.enum(['configured', 'not_configured']),
    }),
    jobQueue: z.object({
        pending: z.number().int().nonnegative(),
        running: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        /** Job hết lượt thử. Con số này khác 0 luôn cần người xem. */
        dead: z.number().int().nonnegative(),
        /** Tuổi của job `pending` lâu nhất, giây. Tăng dần = worker đã chết. */
        oldestPendingSec: z.number().int().nonnegative().nullable(),
        /**
         * Job `running` mà tiến trình cầm nó đã chết — APM-JOB-a.
         *
         * Khác `running` ở ngay trên: đó là số job **đang chạy thật**, còn đây là số
         * job không ai chạy nữa. Hệ thống tự thu hồi chúng ở lượt nhận việc kế
         * tiếp, nên con số này bình thường phải là 0; khác 0 kéo dài nghĩa là vòng
         * nhận việc cũng đã đứng, và lúc đó không có gì tự sửa nữa.
         */
        stalled: z.number().int().nonnegative(),
    }),
    eventLog: z.object({
        hotEventCount: z.number().int().nonnegative(),
        partitions: z.array(z.string()),
        archivedPeriods: z.array(z.string()),
    }),
});
//# sourceMappingURL=audit.js.map